// Record Inspection Panel
// Pull-out panel for recording completed inspections

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, addMonths, addYears } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ComplianceItemCard } from './compliance-item-card';
import { useNavigate } from 'react-router-dom';

// Form validation schema
const formSchema = z.object({
  inspectionDate: z.date({
    message: 'Inspection date is required',
  }),
  inspectorName: z.string().min(1, 'Inspector name is required'),
  outcome: z.enum(['Pass', 'Fail', 'PassWithAdvisories'], {
    message: 'Outcome is required',
  }),
  findings: z.string().optional(),
  actionRequired: z.string().optional(),
  nextInspectionDate: z.date().optional(),
  evidenceFiles: z.array(z.instanceof(File)).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceItem {
  id: number;
  assetId?: string;
  assetName: string;
  assetCode?: string;
  regimeCode?: string;
  regimeName: string;
  complianceCheckId?: number;
  frequency?: string;
}

interface RecordInspectionPanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly complianceItem?: ComplianceItem;
  readonly onSubmit: (data: FormValues) => Promise<void>;
}

export function RecordInspectionPanel({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<RecordInspectionPanelProps>) {
  const navigate = useNavigate();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      inspectionDate: new Date(),
      inspectorName: '',
      outcome: undefined,
      findings: '',
      actionRequired: '',
      nextInspectionDate: undefined,
      evidenceFiles: [],
    },
  });

  const selectedFiles = form.watch('evidenceFiles') || [];
  const outcome = form.watch('outcome');
  const inspectionDate = form.watch('inspectionDate');

  // Auto-calculate next inspection date based on frequency
  const handleOutcomeChange = (value: 'Pass' | 'Fail' | 'PassWithAdvisories') => {
    form.setValue('outcome', value);
    
    if (value === 'Pass' && complianceItem?.frequency && inspectionDate) {
      let nextDate: Date;
      const frequency = complianceItem.frequency.toLowerCase();
      
      if (frequency.includes('month')) {
        const months = parseInt(frequency) || 12;
        nextDate = addMonths(inspectionDate, months);
      } else if (frequency.includes('year')) {
        const years = parseInt(frequency) || 1;
        nextDate = addYears(inspectionDate, years);
      } else {
        nextDate = addYears(inspectionDate, 1);
      }
      
      form.setValue('nextInspectionDate', nextDate);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentFiles = form.getValues('evidenceFiles') || [];
    form.setValue('evidenceFiles', [...currentFiles, ...files]);
  };

  const removeFile = (index: number) => {
    const currentFiles = form.getValues('evidenceFiles') || [];
    form.setValue('evidenceFiles', currentFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      await onSubmit(data);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting inspection:', error);
    }
  };

  const handleNavigateToAsset = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const handleNavigateToRule = (ruleId: number) => {
    navigate(`/compliance/regulations?ruleId=${ruleId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 bg-white dark:bg-black border-b">
          <SheetTitle className="text-lg font-semibold">Record Inspection</SheetTitle>
          <SheetDescription>
            Log a completed inspection for this compliance requirement
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 bg-muted/30">
          <Form {...form}>
            <form id="record-inspection-form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 pb-6 pt-6">
              {/* Compliance Item Info */}
              {complianceItem && (
                <ComplianceItemCard
                  assetId={complianceItem.assetId}
                  assetName={complianceItem.assetName}
                  assetCode={complianceItem.assetCode}
                  regimeCode={complianceItem.regimeCode || 'N/A'}
                  regimeName={complianceItem.regimeName}
                  complianceCheckId={complianceItem.complianceCheckId}
                  onNavigateToAsset={handleNavigateToAsset}
                  onNavigateToRule={handleNavigateToRule}
                />
              )}

              {/* Inspection Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Inspection Date */}
                  <FormField
                    control={form.control}
                    name="inspectionDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="font-semibold">Inspection Date *</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date > new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription className="text-xs">
                          When was the inspection completed?
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Inspector Name */}
                  <FormField
                    control={form.control}
                    name="inspectorName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold">Inspector Name *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter inspector name" 
                            className="bg-white dark:bg-gray-950"
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          Name of the person who performed the inspection
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Outcome */}
                <FormField
                  control={form.control}
                  name="outcome"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Outcome *</FormLabel>
                      <Select onValueChange={handleOutcomeChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-950">
                            <SelectValue placeholder="Select outcome" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Pass">Pass</SelectItem>
                          <SelectItem value="PassWithAdvisories">Pass with Advisories</SelectItem>
                          <SelectItem value="Fail">Fail</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-xs">
                        Result of the inspection
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Findings */}
                <FormField
                  control={form.control}
                  name="findings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold">Findings {outcome === 'Fail' && <span className="text-red-500">*</span>}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter inspection findings..."
                          className="min-h-[100px] bg-white dark:bg-gray-950"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        {outcome === 'Fail' 
                          ? 'Required for failed inspections'
                          : 'Any observations or notes from the inspection'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Action Required (for Fail or PassWithAdvisories) */}
                {(outcome === 'Fail' || outcome === 'PassWithAdvisories') && (
                  <FormField
                    control={form.control}
                    name="actionRequired"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold">Action Required {outcome === 'Fail' && <span className="text-red-500">*</span>}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe required actions..."
                            className="min-h-[80px] bg-white dark:bg-gray-950"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription className="text-xs">
                          {outcome === 'Fail'
                            ? 'Required for failed inspections'
                            : 'Recommended actions based on advisories'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Next Inspection Date */}
                {outcome === 'Pass' && (
                  <FormField
                    control={form.control}
                    name="nextInspectionDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel className="font-semibold">Next Inspection Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  'w-full pl-3 text-left font-normal bg-white dark:bg-gray-950',
                                  !field.value && 'text-muted-foreground'
                                )}
                              >
                                {field.value ? (
                                  format(field.value, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription className="text-xs">
                          Auto-calculated based on frequency (can be adjusted)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Evidence Files */}
                <div className="space-y-2">
                  <Label className="font-semibold">Evidence Files</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 bg-white dark:bg-gray-950">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                      <div className="text-sm text-center">
                        <label htmlFor="file-upload" className="text-primary cursor-pointer hover:underline">
                          Click to upload
                        </label>
                        <span className="text-muted-foreground"> or drag and drop</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        PDF, images, or documents (max 10MB each)
                      </p>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                    </div>
                  </div>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2 mt-3">
                        {selectedFiles.map((file) => (
                          <div
                            key={`${file.name}-${file.size}`}
                            className="flex items-center justify-between p-2 bg-muted rounded-md text-sm"
                          >
                            <span className="truncate flex-1">{file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(selectedFiles.indexOf(file))}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>

        <SheetFooter className="border-t bg-white dark:bg-black px-6 py-4 mt-auto">
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="record-inspection-form"
              className="flex-1"
            >
              Record Inspection
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
